import { AfterViewInit, Component, Inject } from '@angular/core';
import * as angular from 'angular';
import { downgradeComponent } from '@angular/upgrade/static';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { filter, first } from 'rxjs/operators';
import { GenericListType, LoadMoreQuery } from '../../../common/components/lists/generic-list.component';
import { AppState } from '../../../store';
import {
  closeHooksModal,
  getHookEvents,
  getHooksList,
  getHookTypes,
  loadMoreHooksList,
  openHooksModal,
  selectHookType,
} from '../../store/hooks/hooks.actions';
import {
  selectAreAllHooksLoadedFlag,
  selectHooks,
  selectHooksErrorFlag,
  selectHooksIsModalOpenFlag,
  selectHooksLoadingFlag,
} from '../../store/hooks/hooks.selectors';
import { Hook, HookTypesEnum } from '../../hook.models';
import { QueryParamsHooksList } from '../../../common/helper/query-params-generic-list.helper';

@Component({
  selector: 'hooks-list',
  template: `
    <generic-list
      [type]="type"
      [emptyTitle]="'hook.generic-object.empty.title' | translate"
      [emptyDescription]="'hook.generic-object.empty.description' | translate"
      [items]="hooks$ | async"
      [isLoading]="hooksLoading$ | async"
      [hasNewButton]="true"
      (createClick)="openCreateModal()"
      [newBtnText]="'hook.generic-object.buttons.new' | translate"
      (loadMore)="loadMoreHooks($event)"
      [limit]="queryParams.limit"
      [allItemsLoaded]="areAllHooksLoaded$ | async"
      [isError]="hooksError$ | async"
    >
      <ng-template templateName="listItem" let-item>
        <hook-list-item [item]="item" (editClick)="openEditModal($event)"></hook-list-item>
      </ng-template>
    </generic-list>
    <xp-modal
      [isOpen]="isModalOpen$ | async"
      [closeButtonText]="'hook.generic-object.buttons.close' | translate"
      [saveButtonText]="'hook.generic-object.buttons.save' | translate"
      [titleText]="'hook.generic-object.title' | translate"
      [isSmall]="true"
      (close)="onModalClose()"
    >
      <ng-template>
        <hook-form [item]="hook"></hook-form>
      </ng-template>
    </xp-modal>
  `,
})
export class HooksListComponent implements AfterViewInit {
  hook: Partial<Hook> = {
    settings: {},
    events: [],
  };
  type = GenericListType.hooks;
  hooksLoading$ = this.store.select(selectHooksLoadingFlag);
  hooksError$ = this.store.select(selectHooksErrorFlag);
  isModalOpen$ = this.store.select(selectHooksIsModalOpenFlag);
  hooks$ = this.store.select(selectHooks);
  areAllHooksLoaded$ = this.store.select(selectAreAllHooksLoadedFlag);
  queryParams = QueryParamsHooksList;

  constructor(
    @Inject('$rootScope') private $rootScope: any,
    private store: Store<AppState>,
    @Inject('$stateParams') private $stateParams: any,
  ) {
    this.store.dispatch(getHooksList({ params: this.queryParams }));
    this.store.dispatch(getHookTypes());
    this.store.dispatch(getHookEvents());
  }

  ngAfterViewInit() {
    if (this.$rootScope.stateIs('app.account.settings.account.hooks.new')) {
      this.openCreateModal(false);
    }
    if (this.$rootScope.stateIs('app.account.settings.account.hooks.type.new')) {
      this.openCreateModal(false, this.$stateParams.type);
    }
    if (this.$rootScope.stateIs('app.account.settings.account.hooks.type.item.edit')) {
      this.hooks$
        .pipe(
          filter((hooks) => hooks.length > 0),
          first(),
        )
        .subscribe((hooks) => {
          const foundHook = hooks.find((item) => item.id === Number(this.$stateParams.id));
          if (!foundHook) {
            this.$rootScope.stateIs('app.account.settings.account.hooks');
          } else {
            this.openEditModal(foundHook);
          }
        });
    }
  }

  openEditModal(item: Hook): void {
    this.$rootScope.stateGo('app.account.settings.account.hooks.type.item.edit', item);
    this.hook = cloneDeep(item);
    this.store.dispatch(selectHookType({ hookType: item.type }));
    this.store.dispatch(openHooksModal());
  }

  openCreateModal(redirect = true, type?: HookTypesEnum): void {
    if (redirect) {
      this.$rootScope.stateGo('app.account.settings.account.hooks.new');
    }

    this.hook = {
      settings: {},
      events: [],
      active: true,
      type,
    };
    this.store.dispatch(selectHookType({ hookType: type }));
    this.store.dispatch(openHooksModal());
  }

  onModalClose(): void {
    this.$rootScope.stateGo('app.account.settings.account.hooks');
    this.store.dispatch(closeHooksModal());
  }

  loadMoreHooks(query: LoadMoreQuery) {
    const params: any = {
      ...this.queryParams,
      ...query,
    };
    this.store.dispatch(loadMoreHooksList({ params }));
  }
}

((window as any).xplenty as angular.IModule).directive(
  'hooksList',
  downgradeComponent({
    component: HooksListComponent,
  }),
);
